import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, CanActivateChild, CanLoad, Route, Router, RouterStateSnapshot, UrlSegment, UrlTree } from '@angular/router';
import { Observable, of, switchMap } from 'rxjs';
import { AuthService } from 'app/core/auth/auth.service';

@Injectable({
    providedIn: 'root'
})
export class AuthGuard implements CanActivate, CanActivateChild, CanLoad
{
    /**
     * Constructor
     */
    constructor(
        private _authService: AuthService,
        private _router: Router
    )
    {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Can activate
     *
     * @param route
     * @param state
     */
    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> | Promise<boolean> | boolean
    {
        const redirectUrl = state.url === '/sign-out' ? '/' : state.url;
        return this._check(redirectUrl, route);
    }

    /**
     * Can activate child
     *
     * @param childRoute
     * @param state
     */
    canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree
    {
        const redirectUrl = state.url === '/sign-out' ? '/' : state.url;
        return this._check(redirectUrl, childRoute);
    }

    /**
     * Can load
     *
     * @param route
     * @param segments
     */
    canLoad(route: Route, segments: UrlSegment[]): Observable<boolean> | Promise<boolean> | boolean
    {
        return this._check('/', null);
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Check the authenticated status
     *
     * @param redirectURL
     * @param route
     * @private
     */
    private _check(redirectURL: string, route?: ActivatedRouteSnapshot | null): Observable<boolean>
    {
        // Check the authentication status
        return this._authService.check()
                   .pipe(
                       switchMap((authenticated) => {

                           // If the user is not authenticated...
                           if ( !authenticated )
                           {
                               // Find the actual route being accessed by looking at the URL
                               let targetRoute = route;
                               if (route && route.children && route.children.length > 0) {
                                   // Find the route that matches the current URL
                                   const currentUrl = redirectURL;
                                   targetRoute = this._findRouteByUrl(route, currentUrl) || route;
                               }
                               
                               // Check if route has custom redirect configuration
                               if (targetRoute && targetRoute.data && targetRoute.data['authRedirect']) {
                                   const customRedirect = targetRoute.data['authRedirect'];
                                   
                                   // If customRedirect is null/empty, redirect to sign-in
                                   if (!customRedirect || customRedirect === '') {
                                       this._router.navigate(['sign-in'], {queryParams: {redirectURL}});
                                   } else {
                                       // Redirect to custom URL
                                       this._router.navigate([customRedirect]);
                                   }
                               } else {
                                   // Default behavior: redirect to sign-in
                                   this._router.navigate(['sign-in'], {queryParams: {redirectURL}});
                               }

                               // Prevent the access
                               return of(false);
                           }

                                                          // Allow the access
                               return of(true);
                       })
                   );
    }

    /**
     * Find the route that matches the given URL
     *
     * @param route
     * @param url
     * @private
     */
    private _findRouteByUrl(route: ActivatedRouteSnapshot, url: string): ActivatedRouteSnapshot | null {
        // Check if this route matches the URL
        if (route.routeConfig && route.routeConfig.path) {
            const routePath = '/' + route.routeConfig.path;
            if (url === routePath || url.startsWith(routePath + '/')) {
                return route;
            }
        }
        
        // Check children routes
        if (route.children && route.children.length > 0) {
            for (const child of route.children) {
                const found = this._findRouteByUrl(child, url);
                if (found) {
                    return found;
                }
            }
        }
        
        return null;
    }
}
